// The contract profile (compliance proposal, Nov 2026, section 1): one set of
// contract rules per project, filled in by a preset for its contract type and
// changeable per project. Anything the signed contract does not say stays off
// or empty ("defaults of zero"): no preset adds indirect costs or a reserve.
// The API applies a preset when a project is made; the web page offers it on
// "How this project works". Saving never applies a preset by itself.
import type { ContractKind, FundingType, ReportFormat } from "./enums";

export const CONTRACT_PRESETS_IDS = ["service_fee_based", "service_global_price", "grant", "other"] as const;
export type ContractPreset = (typeof CONTRACT_PRESETS_IDS)[number];
export const CONTRACT_PRESET_LABELS: Record<ContractPreset, string> = {
  service_fee_based: "Fee-based service contract",
  service_global_price: "Global-price service contract",
  grant: "Grant",
  other: "Other funder",
};

export const CURRENCY_RULES = ["inforeuro_invoice_month", "inforeuro_payment_month", "bank_rate"] as const;
export type CurrencyRule = (typeof CURRENCY_RULES)[number];
export const CURRENCY_RULE_LABELS: Record<CurrencyRule, string> = {
  inforeuro_invoice_month: "InforEuro rate of the invoice month",
  inforeuro_payment_month: "InforEuro rate of the payment month",
  bank_rate: "The bank's actual rate",
};

export const INVOICE_UNITS = ["whole_days", "half_days"] as const;
export type InvoiceUnit = (typeof INVOICE_UNITS)[number];
export const INVOICE_UNIT_LABELS: Record<InvoiceUnit, string> = { whole_days: "Whole days", half_days: "Half days" };

export const DP_REGIMES = ["gdpr", "eu_2018_1725"] as const;
export type DpRegime = (typeof DP_REGIMES)[number];
export const DP_REGIME_LABELS: Record<DpRegime, string> = { gdpr: "GDPR", eu_2018_1725: "Regulation (EU) 2018/1725" };

export type ServiceKind = "service_fee_based" | "service_global_price";

export type ContractProfile = {
  fundingType: FundingType;
  serviceKind: ServiceKind | null;
  approval: { tors: boolean; expertChanges: boolean; budgetMoves: boolean; limitPct: number };
  indirectPct: number | null;
  reservePct: number | null;
  reporting: { everyMonths: 0 | 6 | 12; dueDays: number; format: ReportFormat; finalDueMonths: number };
  workingDays: { hoursPerDay: number | null; halfDays: boolean; invoiceIn: InvoiceUnit | null };
  currencyRule: CurrencyRule | null;
  /** Years records are kept after the final payment; null = the organisation's policy. */
  retentionYears: number | null;
  dataProtection: { regime: DpRegime | null; controller: string; breachNoticeHours: number | null };
};

// Approvals and the reporting rhythm keep today's defaults (D6: service contracts approve ToRs,
// grants do not; expert changes and money moves above 10% need approval; reports every year,
// due within 45 days). The proposal's examples (grants "mostly off", service reports every
// 6 months) wait for Marius's confirmation: one value each here.
const todaysApproval = (tors: boolean) => ({ tors, expertChanges: true, budgetMoves: true, limitPct: 10 });
const todaysReporting = { everyMonths: 12 as const, dueDays: 45, format: "eu_annex_vi" as const, finalDueMonths: 3 };
const noApproval = { tors: false, expertChanges: false, budgetMoves: false, limitPct: 10 };
const notSetDays = { hoursPerDay: null, halfDays: false, invoiceIn: null };

/** The preset values (plan docs/superpowers/plans/2026-11-02-contract-profile.md, with the ruling above). */
export const CONTRACT_PRESETS: Record<ContractPreset, ContractProfile> = {
  service_fee_based: {
    fundingType: "service",
    serviceKind: "service_fee_based",
    approval: todaysApproval(true),
    indirectPct: null,
    reservePct: null,
    reporting: todaysReporting,
    workingDays: { hoursPerDay: 7, halfDays: true, invoiceIn: "whole_days" },
    currencyRule: "inforeuro_invoice_month",
    retentionYears: 7,
    dataProtection: { regime: "gdpr", controller: "", breachNoticeHours: 48 },
  },
  service_global_price: {
    fundingType: "service",
    serviceKind: "service_global_price",
    approval: todaysApproval(true),
    indirectPct: null,
    reservePct: null,
    reporting: todaysReporting,
    workingDays: notSetDays,
    currencyRule: "inforeuro_invoice_month",
    retentionYears: 7,
    dataProtection: { regime: "gdpr", controller: "", breachNoticeHours: 48 },
  },
  grant: {
    fundingType: "grant",
    serviceKind: null,
    approval: todaysApproval(false),
    indirectPct: null,
    reservePct: null,
    reporting: todaysReporting,
    workingDays: notSetDays,
    currencyRule: null,
    retentionYears: 5,
    dataProtection: { regime: "gdpr", controller: "", breachNoticeHours: null },
  },
  other: {
    fundingType: "other",
    serviceKind: null,
    approval: noApproval,
    indirectPct: null,
    reservePct: null,
    reporting: { everyMonths: 0, dueDays: 45, format: "own", finalDueMonths: 3 },
    workingDays: notSetDays,
    currencyRule: null,
    retentionYears: null,
    dataProtection: { regime: null, controller: "", breachNoticeHours: null },
  },
};

export const presetForKind = (kind: ContractKind): ContractPreset => kind;

/** Whether a preset fits the kind of funding (and, for a service contract, how it is paid). */
export const presetFits = (preset: ContractPreset, fundingType: FundingType, serviceKind: ServiceKind | null): boolean =>
  preset === (fundingType === "service" ? (serviceKind ?? "service_fee_based") : fundingType);

export type ProfileChange = { key: string; label: string; from: string; to: string };

const NOT_SET = "Not set";
const onOff = (v: unknown) => (v ? "On" : "Off");
const pct = (v: unknown) => (v === null ? "None" : `${v}%`);
const unit = (u: string) => (v: unknown) => (v === null ? NOT_SET : `${v} ${u}`);
const label = (map: Record<string, string>) => (v: unknown) => (v === null ? NOT_SET : (map[String(v)] ?? String(v)));
const FUNDING_WORDS: Record<FundingType, string> = { service: "Service contract", grant: "Grant", other: "Other" };
const SERVICE_WORDS: Record<ServiceKind, string> = { service_fee_based: "Fee-based", service_global_price: "Global price" };
const FORMAT_WORDS: Record<ReportFormat, string> = { eu_annex_vi: "EU narrative report (Annex VI)", own: "Our own template" };
const rhythm = (v: unknown) => (v === 6 ? "Every 6 months" : v === 0 ? "Only at the end" : "Every year");

/** Every leaf setting, in the order the page and the log show them: key path, label, how a value reads. */
const FIELDS: { key: string; label: string; get: (p: ContractProfile) => unknown; show: (v: unknown) => string }[] = [
  { key: "fundingType", label: "Kind of funding", get: (p) => p.fundingType, show: label(FUNDING_WORDS) },
  { key: "serviceKind", label: "How the service is paid", get: (p) => p.serviceKind, show: label(SERVICE_WORDS) },
  { key: "approval.tors", label: "ToRs need approval", get: (p) => p.approval.tors, show: onOff },
  { key: "approval.expertChanges", label: "Expert changes need approval", get: (p) => p.approval.expertChanges, show: onOff },
  { key: "approval.budgetMoves", label: "Money moves need approval", get: (p) => p.approval.budgetMoves, show: onOff },
  { key: "approval.limitPct", label: "Limit for money moves", get: (p) => p.approval.limitPct, show: (v) => `${v}% of a heading` },
  { key: "indirectPct", label: "Indirect costs", get: (p) => p.indirectPct, show: pct },
  { key: "reservePct", label: "Reserve", get: (p) => p.reservePct, show: pct },
  { key: "reporting.everyMonths", label: "Reports", get: (p) => p.reporting.everyMonths, show: rhythm },
  { key: "reporting.dueDays", label: "Due within", get: (p) => p.reporting.dueDays, show: unit("days") },
  { key: "reporting.format", label: "Report format", get: (p) => p.reporting.format, show: label(FORMAT_WORDS) },
  { key: "reporting.finalDueMonths", label: "Final report due", get: (p) => p.reporting.finalDueMonths, show: (v) => `${v} months after the end` },
  { key: "workingDays.hoursPerDay", label: "Hours in a working day", get: (p) => p.workingDays.hoursPerDay, show: unit("hours") },
  { key: "workingDays.halfDays", label: "Half days allowed", get: (p) => p.workingDays.halfDays, show: (v) => (v ? "Yes" : "No") },
  { key: "workingDays.invoiceIn", label: "Invoiced in", get: (p) => p.workingDays.invoiceIn, show: label(INVOICE_UNIT_LABELS) },
  { key: "currencyRule", label: "Currency conversion", get: (p) => p.currencyRule, show: label(CURRENCY_RULE_LABELS) },
  { key: "retentionYears", label: "Records kept", get: (p) => p.retentionYears, show: (v) => (v === null ? "The organisation's policy" : `${v} years`) },
  { key: "dataProtection.regime", label: "Data protection law", get: (p) => p.dataProtection.regime, show: label(DP_REGIME_LABELS) },
  { key: "dataProtection.controller", label: "Data controller", get: (p) => p.dataProtection.controller, show: (v) => (v ? String(v) : NOT_SET) },
  { key: "dataProtection.breachNoticeHours", label: "Breach notice within", get: (p) => p.dataProtection.breachNoticeHours, show: unit("hours") },
];

/** One entry per setting that differs, in plain words ("Indirect costs: None → 7%"). */
export function profileChanges(before: ContractProfile, after: ContractProfile): ProfileChange[] {
  return FIELDS.filter((f) => f.get(before) !== f.get(after)).map((f) => ({ key: f.key, label: f.label, from: f.show(f.get(before)), to: f.show(f.get(after)) }));
}

/** The keys of the settings that differ from the preset the project follows; none without a preset. */
export function changedFromPreset(profile: ContractProfile, preset: ContractPreset | null): string[] {
  return preset ? profileChanges(CONTRACT_PRESETS[preset], profile).map((c) => c.key) : [];
}

/**
 * X-04 (audit round 4): co-financing (the share the organisation pays itself) belongs to grants and other funders.
 * A service contract is paid in full for its services: it has none, so no screen shows a co-financing share for it.
 */
export const hasCofinancing = (fundingType: FundingType): boolean => fundingType !== "service";
